import React, { useState, useRef, useEffect } from 'react';
import { Container, Form, Button, Checkbox } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import InputFile from '../InputFile';
import { submitFormData, getBase64, validateInputType, validateInputSize } from '../../helpers/form-api';
import { recaptchaSize } from '../../helpers/common';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

export default function HighVoltageIsolationsForm() {
  const initialFormData = {
    applicant_company_name: '',
    applicant_title: '',
    applicant_given_names: '',
    applicant_family_name: '',
    applicant_email_address: '',
    applicant_contact_number: '',
    applicant_abn: '',
    applicant_address_1: '',
    applicant_address_2: '',
    applicant_suburb: '',
    applicant_state: '',
    applicant_postcode: '',
    customer_company_name: '',
    customer_title: '',
    customer_given_names: '',
    customer_family_name: '',
    customer_email_address: '',
    customer_contact_number: '',
    customer_abn: '',
    customer_address_1: '',
    customer_address_2: '',
    customer_suburb: '',
    customer_state: '',
    customer_postcode: '',
    isolation_reason: '',
    customer_works_description: '',
    prescribed_works: '',
    date_isolation: '',
    date_reconnection: '',
    isolation_type: '',
    site_address: '',
    internal_reference_number: '',
  };
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({
    isolation_type: undefined,
    attachment_1: undefined,
    attachment_2: undefined,
    attachment_3: undefined,
  });
  const attachmentInput1 = useRef(null);
  const attachmentInputWrapper1 = useRef(null);
  const attachmentInput2 = useRef(null);
  const attachmentInputWrapper2 = useRef(null);
  const attachmentInput3 = useRef(null);
  const attachmentInputWrapper3 = useRef(null);
  const recaptchaRef = useRef(null);
  const prescribedWorksMessageRef = useRef(null);

  useEffect(() => {
    if (formData && formData.prescribed_works === 'yes') {
      prescribedWorksMessageRef.current.style.display = 'block';
    } else {
      prescribedWorksMessageRef.current.style.display = 'none';
    }
  }, [formData]);

  function submitText() {
    if (submitting) {
      return 'Submitting...';
    } else if (success) {
      return 'Submitted';
    }
    return 'Submit';
  }

  async function handleSubmit() {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      const valid = validateInputs();
      if (!valid) {
        setError('Missing field.');
        setSubmitting(false);
        return;
      }

      if (captcha) {
        let file1, file2, file3;
        if (attachmentInput1.current) {
          file1 = attachmentInput1.current.files[0];
        }
        if (attachmentInput2.current) {
          file2 = attachmentInput2.current.files[0];
        }
        if (attachmentInput3.current) {
          file3 = attachmentInput3.current.files[0];
        }
        let attachment1, attachment2, attachment3;
        if (file1) {
          attachment1 = {
            content: await getBase64(file1),
            name: file1.name,
          };
        }
        if (file2) {
          attachment2 = {
            content: await getBase64(file2),
            name: file2.name,
          };
        }
        if (file3) {
          attachment3 = {
            content: await getBase64(file3),
            name: file3.name,
          };
        }

        const finalFormData = {
          ...formData,
          attachment1,
          attachment2,
          attachment3,
        };

        const response = await submitFormData('high_voltage_isolations', finalFormData);

        if (response.status !== 200) {
          setSuccess(null);
          setError(response.message);
          setSubmitting(false);
        } else {
          setSuccess('Success');
          setError(null);
          setSubmitting(false);
          setFormData(initialFormData);

          if (attachmentInputWrapper1.current) {
            attachmentInputWrapper1.current.clear();
          }
          if (attachmentInputWrapper2.current) {
            attachmentInputWrapper2.current.clear();
          }
          if (attachmentInputWrapper3.current) {
            attachmentInputWrapper3.current.clear();
          }
        }

        setCaptcha(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      } else {
        setSuccess(null);
        setError('Please tick ReCAPTCHA');
        setSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);

      setSuccess(null);
      setError('Error');
      setSubmitting(false);
    }
  }

  const handleSelectChange = (event, { name, value }) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const updateFormData = (event) => {
    setFormData({
      ...formData,
      [event.currentTarget.name]: event.currentTarget.value,
    });
  };

  const handleCaptcha = (value) => {
    setCaptcha(value);
    setFormData({
      ...formData,
      captchaResponse: value,
    });
  };

  const validateInputs = () => {
    let valid = true;

    const { isolation_type } = formData;

    if (!isolation_type) {
      setFormErrors({ ...formErrors, isolation_type: 'Required' });
      valid = false;
    } else {
      setFormErrors({ ...formErrors, isolation_type: undefined });
    }

    let file1, file2, file3;
    if (attachmentInput1.current) {
      file1 = attachmentInput1.current.files[0];
      if (file1 && (!validateInputType(file1.type) || !validateInputSize(file1.size, 5))) {
        setFormErrors({ ...formErrors, attachment1: 'FileType' });
        valid = false;
      } else {
        setFormErrors({ ...formErrors, attachment1: undefined });
      }
    }

    if (attachmentInput2.current) {
      file2 = attachmentInput2.current.files[0];
      if (file2 && (!validateInputType(file2.type) || !validateInputSize(file2.size, 5))) {
        setFormErrors({ ...formErrors, attachment2: 'FileType' });
        valid = false;
      } else {
        setFormErrors({ ...formErrors, attachment2: undefined });
      }
    }
    if (attachmentInput3.current) {
      file3 = attachmentInput3.current.files[0];
      if (file3 && (!validateInputType(file3.type) || !validateInputSize(file3.size, 5))) {
        setFormErrors({ ...formErrors, attachment3: 'FileType' });
        valid = false;
      } else {
        setFormErrors({ ...formErrors, attachment3: undefined });
      }
    }
    return valid;
  };

  function copyApplicantData(data) {
    if (data.checked) {
      setFormData({
        ...formData,
        customer_company_name: applicant_company_name,
        customer_title: applicant_title,
        customer_given_names: applicant_given_names,
        customer_family_name: applicant_family_name,
        customer_email_address: applicant_email_address,
        customer_contact_number: applicant_contact_number,
        customer_abn: applicant_abn,
        customer_address_1: applicant_address_1,
        customer_address_2: applicant_address_2,
        customer_suburb: applicant_suburb,
        customer_state: applicant_state,
        customer_postcode: applicant_postcode,
      });
    }
  }

  const {
    applicant_company_name,
    applicant_title,
    applicant_given_names,
    applicant_family_name,
    applicant_email_address,
    applicant_contact_number,
    applicant_abn,
    applicant_address_1,
    applicant_address_2,
    applicant_suburb,
    applicant_state,
    applicant_postcode,
    customer_company_name,
    customer_title,
    customer_given_names,
    customer_family_name,
    customer_email_address,
    customer_contact_number,
    customer_abn,
    customer_address_1,
    customer_address_2,
    customer_suburb,
    customer_state,
    customer_postcode,
    isolation_reason,
    customer_works_description,
    prescribed_works,
    date_isolation,
    date_reconnection,
    isolation_type,
    site_address,
    internal_reference_number,
  } = formData;

  const {
    isolation_type: isolationTypeError,
    attachment_1: attachment1Error,
    attachment_2: attachment2Error,
    attachment_3: attachment3Error,
  } = formErrors;

  return (
    <section>
      <Container>
        <div className="grid-wrapper">
          <div className="form-container">
            <Form onSubmit={handleSubmit}>
              <h4>Please enter application details</h4>
              <Form.Input
                label="Company name"
                required
                name="applicant_company_name"
                type="text"
                value={applicant_company_name}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-company-name"
              />

              <Form.Input
                label="Title"
                required
                name="applicant_title"
                type="text"
                value={applicant_title}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-title"
              />

              <Form.Input
                label="Given name(s)"
                required
                name="applicant_given_names"
                type="text"
                value={applicant_given_names}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-given-names"
              />

              <Form.Input
                label="Family name"
                required
                name="applicant_family_name"
                type="text"
                value={applicant_family_name}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-family-name"
              />

              <Form.Input
                label="Email Address"
                required
                name="applicant_email_address"
                type="email"
                value={applicant_email_address}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-email-address"
              />

              <Form.Input
                label="Contact number"
                required
                name="applicant_contact_number"
                type="phone"
                value={applicant_contact_number}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-contact-number"
              />

              <Form.Input
                label="ABN (for commercial customers only)"
                name="applicant_abn"
                value={applicant_abn}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-abn"
              />

              <Form.Input
                label="Address Line 1"
                required
                name="applicant_address_1"
                value={applicant_address_1}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-address-1"
              />

              <Form.Input
                label="Address Line 2"
                name="applicant_address_2"
                value={applicant_address_2}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-address-2"
              />

              <Form.Input
                label="Suburb"
                required
                name="applicant_suburb"
                value={applicant_suburb}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-suburb"
              />

              <Form.Input
                label="State"
                required
                name="applicant_state"
                value={applicant_state}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-state"
              />

              <Form.Input
                label="Postcode"
                required
                name="applicant_postcode"
                value={applicant_postcode}
                onChange={(e) => updateFormData(e)}
                id="form-applicant-postcode"
              />

              <Form.Field>
                <Checkbox
                  label="Copy applicant details to customer"
                  id="form-copy-applicant"
                  onChange={(event, data) => copyApplicantData(data)}
                />
              </Form.Field>

              <h4>Please enter customer details</h4>

              <Form.Input
                label="Company Name"
                required
                name="customer_company_name"
                value={customer_company_name}
                onChange={(e) => updateFormData(e)}
                id="form-customer-company-name"
              />

              <Form.Input
                label="Title"
                required
                name="customer_title"
                type="text"
                value={customer_title}
                onChange={(e) => updateFormData(e)}
                id="form-customer-title"
              />

              <Form.Input
                label="Given name(s)"
                required
                name="customer_given_names"
                type="text"
                value={customer_given_names}
                onChange={(e) => updateFormData(e)}
                id="form-customer-given-names"
              />

              <Form.Input
                label="Family name"
                required
                name="customer_family_name"
                type="text"
                value={customer_family_name}
                onChange={(e) => updateFormData(e)}
                id="form-customer-family-name"
              />

              <Form.Input
                label="Email Address"
                required
                name="customer_email_address"
                type="email"
                value={customer_email_address}
                onChange={(e) => updateFormData(e)}
                id="form-customer-email-address"
              />

              <Form.Input
                label="Contact number"
                required
                name="customer_contact_number"
                type="phone"
                value={customer_contact_number}
                onChange={(e) => updateFormData(e)}
                id="form-customer-contact-number"
              />

              <Form.Input
                label="ABN (for commercial customers only)"
                name="customer_abn"
                value={customer_abn}
                onChange={(e) => updateFormData(e)}
                id="form-customer-abn"
              />

              <Form.Input
                label="Address Line 1"
                required
                name="customer_address_1"
                value={customer_address_1}
                onChange={(e) => updateFormData(e)}
                id="form-customer-address-1"
              />

              <Form.Input
                label="Address Line 2"
                name="customer_address_2"
                value={customer_address_2}
                onChange={(e) => updateFormData(e)}
                id="form-customer-address-2"
              />

              <Form.Input
                label="Suburb"
                required
                name="customer_suburb"
                value={customer_suburb}
                onChange={(e) => updateFormData(e)}
                id="form-customer-suburb"
              />

              <Form.Input
                label="State"
                required
                name="customer_state"
                value={customer_state}
                onChange={(e) => updateFormData(e)}
                id="form-customer-state"
              />

              <Form.Input
                label="Postcode"
                required
                name="customer_postcode"
                value={customer_postcode}
                onChange={(e) => updateFormData(e)}
                id="form-customer-postcode"
              />

              <Form.Input
                label="Site Address"
                required
                name="site_address"
                value={site_address}
                onChange={(e) => updateFormData(e)}
                id="form-site-address"
              />

              <Form.TextArea
                label="Please provide a reason for the isolation"
                required
                name="isolation_reason"
                value={isolation_reason}
                onChange={(e) => updateFormData(e)}
                id="form-isolation-reason"
              />

              <Form.TextArea
                label="Please provide a detailed description of customer works"
                required
                name="customer_works_description"
                value={customer_works_description}
                onChange={(e) => updateFormData(e)}
                id="form-customer-works-description"
              />

              <Form.Field>
                <Form.Select
                  label="Will any prescribed works be completed?"
                  required
                  name="prescribed_works"
                  value={prescribed_works}
                  placeholder="Please select"
                  onChange={(e, s) => handleSelectChange(e, s)}
                  id="form-prescribed-works"
                  options={[
                    {
                      key: 1,
                      text: 'Yes',
                      value: 'yes',
                    },
                    { key: 2, text: 'No', value: 'no' },
                  ]}
                  error={isolationTypeError != null}
                />
                <p ref={prescribedWorksMessageRef} className="form-prescribed-works-message">
                  We will be in contact regarding your application
                </p>
              </Form.Field>

              <Form.Input
                label="Preferred date and time of isolation"
                required
                className="calendar-icon"
                name="date_isolation"
                type="datetime-local"
                value={date_isolation}
                onChange={(e) => updateFormData(e)}
                id="form-date-isolation"
              />

              <Form.Input
                label="Preferred date and time of reconnection"
                required
                className="calendar-icon"
                name="date_reconnection"
                type="datetime-local"
                value={date_reconnection}
                onChange={(e) => updateFormData(e)}
                id="form-date-reconnection"
              />

              <Form.Select
                label="Do you require an isolation of"
                required
                name="isolation_type"
                value={isolation_type}
                placeholder="Please select"
                onChange={(e, s) => handleSelectChange(e, s)}
                id="form-isolation-type"
                options={[
                  {
                    key: 1,
                    text: 'SWER substation',
                    value: 'swer-substation',
                  },
                  { key: 2, text: 'HV supply', value: 'hv-supply' },
                ]}
                error={isolationTypeError != null}
              />

              <Form.Input
                label="Internal reference number"
                name="internal_reference_number"
                value={internal_reference_number}
                onChange={(e) => updateFormData(e)}
                id="form-internal-reference-number"
              />

              <InputFile
                label="File Attachment 1"
                name="file_1"
                text="No file chosen"
                restrictions="Max. filesize 1MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                id="form-file-1"
                maxFileSize={1}
                refProp={attachmentInput1}
                ref={attachmentInputWrapper1}
                error={attachment1Error != null}
              />

              <InputFile
                label="File Attachment 2"
                name="file_2"
                text="No file chosen"
                restrictions="Max. filesize 1MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                id="form-file-2"
                maxFileSize={1}
                refProp={attachmentInput2}
                ref={attachmentInputWrapper2}
                error={attachment2Error != null}
              />

              <InputFile
                label="File Attachment 3"
                name="file_3"
                text="No file chosen"
                restrictions="Max. filesize 1MB. Please upload only pdf, doc, docx, xls, xslx, pages, numbers, jpg, tiff, bmp, png, txt, csv, rtf, odt or ods files. Note that if form submission fails, please re-upload attachment."
                id="form-file-3"
                maxFileSize={1}
                refProp={attachmentInput3}
                ref={attachmentInputWrapper3}
                error={attachment3Error != null}
              />

              <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaKey} onChange={(e) => handleCaptcha(e)} size={recaptchaSize()} />

              <Button className="form--submit-btn" disabled={submitting} loading={submitting}>
                {submitText()}
              </Button>

              {success ? <div className="success-message">{success}</div> : null}
              {error ? <div className="error-message">{error}</div> : null}
            </Form>
          </div>
        </div>
      </Container>
    </section>
  );
}
